const ISO_DURATION_REGEX =
  /^P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

type DurationUnits = {
  years: number;
  months: number;
  weeks: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const normalizeIsoPeriod = (period?: string): string | null => {
  if (!period?.startsWith('P')) {
    return null;
  }

  const upperCasePeriod = period.toUpperCase().trim();

  if (
    !upperCasePeriod.includes('T') &&
    /^P\d+(?:\.\d+)?H$/.test(upperCasePeriod)
  ) {
    return upperCasePeriod.replace(/^P/, 'PT');
  }

  return upperCasePeriod;
};

const extractIsoDurationUnits = (period?: string): DurationUnits | null => {
  const normalized = normalizeIsoPeriod(period);
  if (!normalized) {
    return null;
  }

  const match = ISO_DURATION_REGEX.exec(normalized);
  if (!match) {
    return null;
  }

  const [, years, months, weeks, days, hours, minutes, seconds] = match;

  const parse = (value?: string) =>
    value !== undefined ? Number.parseFloat(value) : 0;

  return {
    years: parse(years),
    months: parse(months),
    weeks: parse(weeks),
    days: parse(days),
    hours: parse(hours),
    minutes: parse(minutes),
    seconds: parse(seconds),
  };
};

const HOURS_IN_DAY = 24;
const HOURS_IN_WEEK = HOURS_IN_DAY * 7;
const HOURS_IN_MONTH = HOURS_IN_DAY * 30; // Should in theory not get used, as months are not expected in rental periods, needs to be checked
const HOURS_IN_YEAR = HOURS_IN_DAY * 365; // Should in theory not get used, as years are not expected in rental periods

const convertDurationUnitsToHours = (units: DurationUnits): number =>
  units.years * HOURS_IN_YEAR +
  units.months * HOURS_IN_MONTH +
  units.weeks * HOURS_IN_WEEK +
  units.days * HOURS_IN_DAY +
  units.hours +
  units.minutes / 60 +
  units.seconds / 3600;

export const parseIsoPeriod = (
  period?: string,
): { intervalCount: string | null; intervalUnit: string | null } => {
  const units = extractIsoDurationUnits(period);

  const orderedUnits = [
    { value: units?.years ?? 0, unit: 'YEAR' },
    { value: units?.months ?? 0, unit: 'MONTH' },
    { value: units?.weeks ?? 0, unit: 'WEEK' },
    { value: units?.days ?? 0, unit: 'DAY' },
    { value: units?.hours ?? 0, unit: 'HOUR' },
    { value: units?.minutes ?? 0, unit: 'MINUTE' },
  ];

  const firstMatchingUnit = orderedUnits.find((entry) => entry.value > 0);

  if (!firstMatchingUnit) {
    return { intervalCount: null, intervalUnit: null };
  }

  return {
    intervalCount: `${firstMatchingUnit.value}`,
    intervalUnit: firstMatchingUnit.unit,
  };
};

export const isoPeriodToHours = (period?: string): number | null => {
  const units = extractIsoDurationUnits(period);
  if (!units) {
    return null;
  }

  const totalHours = convertDurationUnitsToHours(units);
  return totalHours > 0 ? totalHours : null;
};

export const intervalToHours = (
  intervalCount: string | null,
  intervalUnit: string | null,
): number | null => {
  if (!intervalCount || !intervalUnit) {
    return null;
  }

  const count = Number.parseFloat(intervalCount);
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }

  switch (intervalUnit) {
    case 'HOUR':
      return count;
    case 'DAY':
      return count * HOURS_IN_DAY;
    case 'WEEK':
      return count * HOURS_IN_WEEK;
    case 'MONTH':
      return count * HOURS_IN_MONTH;
    case 'YEAR':
      return count * HOURS_IN_YEAR;
    case 'MINUTE':
      return count / 60;
    default:
      return null;
  }
};
